import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

/** Scrollable page column inside the auth dialog's right pane. */
export const FormPage = styled(Box)({
  width: '100%',
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '48px 40px',
  boxSizing: 'border-box',
  overflowY: 'auto',
})

/** Centered form column — mirrors the mockup's `.form-wrap` (360px). */
export const FormWrap = styled(Box)({
  width: '100%',
  maxWidth: 360,
  margin: '0 auto',
})

export const FormTitle = styled(Typography)({
  fontSize: 26,
  fontWeight: 720,
  letterSpacing: '-0.03em',
  color: 'var(--ds-ink)',
  margin: '0 0 6px',
})

export const FormSub = styled(Typography)({
  fontSize: 14,
  color: 'var(--ds-ink-muted)',
  marginBottom: 26,
  lineHeight: 1.5,
  '& b': { color: 'var(--ds-ink)', fontWeight: 650 },
})

export const SsoStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 9,
  marginBottom: 22,
})

export const SsoButton = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  height: 44,
  width: '100%',
  borderRadius: 11,
  boxShadow: 'inset 0 0 0 1px var(--ds-border-strong)',
  fontSize: 13.5,
  fontWeight: 600,
  color: 'var(--ds-ink)',
  background: 'var(--ds-canvas)',
  cursor: 'pointer',
  transition: 'box-shadow var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': { boxShadow: 'inset 0 0 0 1px var(--ds-ink)' },
})

export const GoogleGlyph = styled('span')({
  width: 16,
  height: 16,
  borderRadius: 3,
  flexShrink: 0,
  background: 'conic-gradient(#ea4335 0 25%, #4285f4 0 50%, #34a853 0 75%, #fbbc05 0)',
})

export const OrDivider = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  margin: '22px 0',
  color: 'var(--ds-ink-subtle)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: 1,
    background: 'var(--ds-border)',
  },
})

export const FieldWrap = styled(Box)({ marginBottom: 14 })

export const FieldLabel = styled('div')({
  fontSize: 12.5,
  fontWeight: 640,
  marginBottom: 7,
  color: 'var(--ds-ink)',
})

/**
 * Shared `sx` for `<Input variant='filled' />` on auth screens — matches
 * `03-auth.html`'s `.fld input` (44px tall, white/bordered, violet focus
 * ring) instead of the DS default gray-filled look, whose `12px 16px`
 * padding plus MUI's own input padding renders ~56px tall — too tall
 * next to the 44px SSO buttons it sits below.
 */
export const authFieldSx = {
  height: 44,
  padding: '0 14px',
  fontSize: 14,
  backgroundColor: '#fff',
  boxShadow: 'inset 0 0 0 1px var(--ds-border-strong)',
  '& .MuiInputBase-input': { padding: 0, fontSize: 14 },
  '&.Mui-focused': {
    boxShadow: 'inset 0 0 0 1.5px var(--ds-accent-deep-b)',
  },
}

/** `authFieldSx` with room on the right for `PasswordToggle`'s eye icon. */
export const authPasswordFieldSx = { ...authFieldSx, paddingRight: 40 }

export const FieldError = styled(Typography)({
  fontSize: 12,
  color: 'var(--ds-danger)',
  marginTop: 6,
})

export const PasswordFieldBox = styled(Box)({ position: 'relative', display: 'flex' })

export const PasswordToggle = styled('button')({
  all: 'unset',
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  cursor: 'pointer',
  color: 'var(--ds-ink-subtle)',
  display: 'flex',
  '&:hover': { color: 'var(--ds-ink-muted)' },
})

export const ErrorBanner = styled(Box)({
  padding: '12px 14px',
  borderRadius: 11,
  background: 'color-mix(in srgb, var(--ds-danger) 10%, var(--ds-canvas))',
  border: '1px solid color-mix(in srgb, var(--ds-danger) 30%, transparent)',
  marginBottom: 14,
})

export const LegalText = styled(Typography)({
  marginTop: 20,
  fontSize: 11.5,
  color: 'var(--ds-ink-subtle)',
  lineHeight: 1.6,
  '& a': { textDecoration: 'underline', textUnderlineOffset: '2px', color: 'inherit' },
})

export const SwapRow = styled(Box)({
  marginTop: 26,
  textAlign: 'center',
  fontSize: 13,
  color: 'var(--ds-ink-muted)',
  '& a': { fontWeight: 650, color: 'var(--ds-ink)', textDecoration: 'none' },
})

export const FieldLinkRow = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: 18,
  '& a': {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--ds-ink)',
    textDecoration: 'none',
  },
})
